import { DialogCreators } from "../../../utils/dialog-creators";
import { IStoreCardData } from "./store-selector.interface";
import { IStoreLocation, IStoreLocationHour } from "../../../models/data.interface";
import { IFormattedAddress } from "../../../utils/util.interface";
import { Util } from "../../../utils/util";
import { StoreStatusId } from "../../../types/enums";
import { Data } from "../../../models/data";
import { LocalizationService } from "../../../services/localization-service";
import { IName } from "../../../interfaces/global.interfaces";
import { Context } from "../../../models/context";

export class StoreSelectorUtil {
    private static _localizationService: LocalizationService;
    private static _data: Data;
    private static _context: Context;

    static init(data: Data, localizationService: LocalizationService, context: Context) {
        this._data = data;
        this._localizationService = localizationService;
        this._context = context;
    }

    static createStoreCardElement(
        storeLocation: IStoreLocation,
        onSelectStore?: (storeKey: string) => void,
        onStartOrderButtonClick?: (storeKey: string) => void,
    ): HTMLElement {
        const storeStatusHtml = this._getStoreStatusHtml(storeLocation.statusId);
        const storeCardData: IStoreCardData = this.getStoreCardData(
            storeLocation,
            onSelectStore,
            onStartOrderButtonClick,
        );
        const addressObject = this._formatAddress(storeLocation);
        const distanceSpanHtml = this._getDistanceHtml(storeLocation.distance, storeLocation.storeKey);
        const hoursInfoHtml = this._getHoursHtml(
            storeLocation.businessStartMinute,
            storeLocation.hours,
            storeLocation.storeKey,
        );

        const id = `div_store_selector_store_${storeLocation.storeKey}`;
        const errorId = `store_error_${storeLocation.storeKey}`;

        const storeItemHtml = `
            <div id="${id}" class="store-address" ${storeCardData.enabled ? "" : "disabled"} tabindex="0" aria-describedby="${errorId}">
                <div>
                    <div class="store-section">
                        <h4>${addressObject.address1}</h4>
                        ${addressObject.address3 ? `<p>${addressObject.address3}</p>` : ""}
                        <p>${addressObject.cityState}</p>
                        <p>${distanceSpanHtml} <span aria-hidden="true">&#8226;</span> ${hoursInfoHtml}</p>
                        ${storeStatusHtml}
                    </div>
                </div>
            </div>`;

        const div = Util.createHtmlElementFromTemplate(storeItemHtml);
        div.onclick = () => onSelectStore?.(storeLocation.storeKey);

        if (onStartOrderButtonClick) {
            const startOrderButtonContainer = div.firstElementChild as HTMLDivElement;
            const startOrderButton = this._createStoreCardStartOrderButton(storeCardData);
            startOrderButtonContainer?.appendChild(startOrderButton);
        }
        return div;
    }

    private static readonly _createStoreCardStartOrderButton = (
        storeCardData: IStoreCardData,
    ): HTMLButtonElement => {
        const button = document.createElement("button");

        button.textContent = storeCardData.buttonText;
        button.setAttribute("ltag", storeCardData.buttonLtag || "ChooseStore");

        button.onclick = (event) => {
            event.stopPropagation();
            storeCardData.onStartOrderButtonClick?.(storeCardData.storeKey);
        };
        return button;
    };

    static getStoreCardData(
        storeLocation: IStoreLocation,
        onSelectStore?: (storeKey: string) => void,
        onStartOrderButtonClick?: (storeKey: string) => void,
    ): IStoreCardData {
        const addressObject = this._formatAddress(storeLocation);
        const result: IStoreCardData = {
            storeKey: storeLocation.storeKey,
            name: storeLocation.name,
            enabled: storeLocation.statusId === StoreStatusId.ACTIVE,
            address1: addressObject.address1,
            address3: addressObject.address3,
            cityState: addressObject.cityState,
            distance: storeLocation.distance,
            buttonText: this._localizationService.names("ChooseStore"),
            buttonLtag: "ChooseStore",
            onSelectStore,
            onStartOrderButtonClick,
        };
        return result;
    }

    private static _formatAddress(store: IStoreLocation): IFormattedAddress {
        return Util.formatAddressObject(
            {
                StreetNo: store.streetNumber,
                Address1: store.address1,
                Address2: store.address2,
                Address3: store.address3,
                Address4: store.address4,
                Address5: store.address5,
                City: store.city,
                State: store.state,
                ZipCode: store.zip,
                CountryID: store.countryId || 1,
                AddressTypeID: 0,
            },
            this._data.getBrandProperty("Countries"),
        );
    }

    private static readonly _getStoreStatusHtml = (statusId: StoreStatusId): string => {
        const statusMap = {
            [StoreStatusId.ACTIVE]: "",
            [StoreStatusId.TEMPORARILY_CLOSED]: "TemporarilyClosed",
            [StoreStatusId.CLOSED]: "",
            [StoreStatusId.INACTIVE]: "Inactive",
            [StoreStatusId.COMING_SOON]: "ComingSoon",
        };
        const statusKey = statusMap[statusId];
        return statusKey ? `<h3 ltag="${statusKey}">${this._localizationService.names(statusKey)}</h3>` : "";
    };

    private static _getDistanceHtml(distance: number, storeKey: string): string {
        const distanceText = this._createDistanceText(distance);
        return `<span id="spn_locations_store_distance_${storeKey}" ltagj="${distanceText.ltagj}">${distanceText.text}</span>`;
    }

    private static readonly _createDistanceText = (distance: number): { text: string; ltagj: string } => {
        const countryId = this._data.getBrandProperty("StoreInfo").countryId;
        const isMetric =
            this._data.getBrandProperty("Countries").find((c) => c.CountryID == countryId)
                ?.UnitOfMeasurement === "Metric";
        const formattedDistance = isMetric ? Util.Float2(this._convertMilesToKilometers(distance)) : distance;
        const units = this._getDistanceUnits(formattedDistance, isMetric);

        const languages = this._localizationService.getSupportedLanguages();
        const nameObj: IName[] = languages.map((lang) => {
            const tempText = this._localizationService
                .names("StoreDistanceAway", lang)
                .replace("{distance}", `${formattedDistance}`)
                .replace("{type}", this._localizationService.names(units, lang));
            return { CULT: lang, NAM: tempText };
        });

        const text = this._localizationService
            .names("StoreDistanceAway")
            .replace("{distance}", `${formattedDistance}`)
            .replace("{type}", this._localizationService.names(units));
        return { text, ltagj: Util.toLtagj(nameObj) };
    };

    private static _convertMilesToKilometers(miles: number): number {
        const conversionFactor = 1.60934;
        return miles * conversionFactor;
    }

    private static readonly _getDistanceUnits = (distance: number, isMetric: boolean): string => {
        if (isMetric) return distance === 1 ? "kilometer" : "kilometers";

        return distance === 1 ? "mile" : "miles";
    };

    private static readonly _getHoursHtml = (
        businessStartMinute: number,
        hours: IStoreLocationHour[],
        storeKey: string,
    ): string => {
        const hoursInfo = this._createHoursText(businessStartMinute, hours);
        return `<span id="spn_locations_store_status_${storeKey}" ltagj="${hoursInfo.ltagj}">${hoursInfo.text}</span>`;
    };

    private static readonly _createHoursText = (
        businessStartMinute: number,
        hours: IStoreLocationHour[],
    ): { text: string; ltagj: string } => {
        const { text, time, weekday } = this._getHoursInfo(businessStartMinute, hours);

        const languages = this._localizationService.getSupportedLanguages();
        const nameObj: IName[] = languages.map((lang) => {
            const tempText = this._localizationService
                .names(text, lang)
                .replace("{time}", time)
                .replace("{weekday}", this._localizationService.names(weekday, lang));
            return { CULT: lang, NAM: tempText };
        });

        const displayText = this._localizationService
            .names(text)
            .replace("{time}", time)
            .replace("{weekday}", this._localizationService.names(weekday));
        return { text: displayText, ltagj: Util.toLtagj(nameObj) };
    };

    private static readonly _getHoursInfo = (
        businessStartMinute: number,
        hours: IStoreLocationHour[],
    ): { text: string; time: string; weekday: string } => {
        const currentDateTime = Util.getBusinessDateManual(
            this._data.getBrandProperty("TimeOffset"),
            businessStartMinute,
        );
        const currentDay = currentDateTime.getDay();
        const currentTimeInMinutes = Util.getNowStoreMinutesManual(0, businessStartMinute);

        const todayHours = hours.find((h) => h.weekdayId === currentDateTime.getDay());

        if (todayHours) {
            if (currentTimeInMinutes < todayHours.startMinute) {
                // Store will open later today
                return {
                    text: "OpensAt",
                    time: Util.formatTimeByIsoCode(
                        todayHours.startMinute,
                        this._context.getProperty("languageCode"),
                    ),
                    weekday: "",
                };
            } else if (currentTimeInMinutes < todayHours.endMinute) {
                // Store is currently open
                return {
                    text: "OpenUntil",
                    time: Util.formatTimeByIsoCode(
                        todayHours.endMinute,
                        this._context.getProperty("languageCode"),
                    ),
                    weekday: "",
                };
            }
        }

        // Store is closed, find the next opening day
        for (let i = 1; i <= 7; i++) {
            const nextDay = (currentDay + i) % 7;
            const nextDayHours = hours.find((h) => h.weekdayId === nextDay);
            if (nextDayHours) {
                return {
                    text: "ClosedOpensAt",
                    time: Util.formatTimeByIsoCode(
                        nextDayHours.startMinute,
                        this._context.getProperty("languageCode"),
                    ),
                    weekday: Util.getWeekdayNameById(nextDay),
                };
            }
        }

        return { text: "Closed", time: "", weekday: "" };
    };

    // Utility functions for Distance Validation
    public static readonly _showDistanceWarningDialog = (
        store: IStoreLocation,
        onContinue: () => void,
        onCancel: () => void,
    ): void => {
        const addressObj = this._formatAddress(store);
        const storeAddress = `${addressObj.address1} ${addressObj.cityState}`;

        let distance: string = this._createDistanceText(store.distance).text;
        distance = `<span class="highlight">${distance}</span>`;

        const dialogMessage: string = this._localizationService
            .names("StoreDistanceDialogMessage_StoreSelector")
            .replace("{{store_address}}", storeAddress)
            .replace("{{store_distance}}", distance);

        const noButtonText = this._localizationService.names("No");
        const yesButtonText = this._localizationService.names("Yes");

        DialogCreators.messageBox(dialogMessage, [
            { text: noButtonText, callBack: onCancel },
            { text: yesButtonText, callBack: onContinue },
        ]);
    };
}
